package org.gymly.ui.admin

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.horizontalScroll
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.outlined.Close
import androidx.compose.material.icons.outlined.Delete
import androidx.compose.material.icons.outlined.Edit
import androidx.compose.material.icons.outlined.Search
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.Checkbox
import androidx.compose.material3.CheckboxDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.DropdownMenu
import androidx.compose.material3.DropdownMenuItem
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.OutlinedButton
import androidx.compose.material3.OutlinedTextField
import androidx.compose.material3.OutlinedTextFieldDefaults
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.compose.ui.window.Dialog
import kotlinx.coroutines.launch
import org.gymly.data.AdminRepo
import org.gymly.i18n.label
import org.gymly.i18n.storeReportsStrings
import org.gymly.i18n.strings
import org.gymly.model.AdminProduct
import org.gymly.model.AdminProductCategoryItem
import org.gymly.model.AdminProductStockLevel
import org.gymly.model.AdminProductUpsert
import org.gymly.theme.AdminColors
import org.gymly.theme.GymlySpace
import org.gymly.util.formatMoney
import org.gymly.util.userMessage
import kotlin.math.roundToLong
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant
import org.gymly.ui.components.VerticalScrollColumn

private enum class ProductsSection(val label: String) {
    Catalog("Productos"),
    Categories("Categorías"),
}

private enum class ProductsDateFilter(val days: Int?) {
    Days7(7),
    Days30(30),
    All(null),
}

private sealed class ProductsDialog {
    data object Create : ProductsDialog()
    data class Edit(val item: AdminProduct) : ProductsDialog()
    data class Detail(val item: AdminProduct) : ProductsDialog()
    data class Delete(val item: AdminProduct) : ProductsDialog()
}

private fun eurosToCents(raw: String): Long? {
    val cleaned = raw.trim().replace("€", "").replace(" ", "").replace(",", ".")
    if (cleaned.isEmpty()) return 0L
    val value = cleaned.toDoubleOrNull() ?: return null
    if (value < 0) return null
    return (value * 100.0).roundToLong()
}

private fun centsToEurosInput(cents: Long): String {
    val scaled = ((cents / 100.0) * 100.0).roundToLong()
    val intPart = scaled / 100
    val frac = kotlin.math.abs(scaled % 100).toInt()
    return "$intPart,${frac.toString().padStart(2, '0')}"
}

@Composable
fun AdminProductsScreen() {
    val t = strings()
    val tr = storeReportsStrings()
    val scope = rememberCoroutineScope()
    var section by remember { mutableStateOf(ProductsSection.Catalog) }
    var categories by remember { mutableStateOf<List<AdminProductCategoryItem>>(emptyList()) }
    var items by remember { mutableStateOf<List<AdminProduct>>(emptyList()) }
    var companyId by remember { mutableStateOf<String?>(null) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var query by remember { mutableStateOf("") }
    var dateFilter by remember { mutableStateOf(ProductsDateFilter.All) }
    var dialog by remember { mutableStateOf<ProductsDialog?>(null) }
    var saving by remember { mutableStateOf(false) }
    val pendingApprovals = LocalPendingApprovalNotifier.current

    fun reload() {
        scope.launch {
            loading = true
            error = null
            try {
                items = AdminRepo.api.listProducts()
                categories = runCatching { AdminRepo.api.listProductCategories() }.getOrDefault(emptyList())
                companyId = runCatching { AdminRepo.api.getCompany().id }.getOrElse {
                    AdminRepo.api.listBranches().firstOrNull()?.id
                }
            } catch (e: Exception) {
                error = e.userMessage(tr.productsLoadFailed)
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(LocalAdminDataRevision.current) { reload() }

    val filtered = remember(items, query, dateFilter) {
        val q = query.trim().lowercase()
        val cutoff = dateFilter.days?.let { Clock.System.now() - it.days }
        items.filter { p ->
            val hay = listOfNotNull(p.name, p.categoryLabel, p.description).joinToString(" ").lowercase()
            val matchesQuery = q.isEmpty() || hay.contains(q)
            val matchesDate = if (cutoff == null) {
                true
            } else {
                val created = p.createdAt?.let { runCatching { Instant.parse(it) }.getOrNull() }
                created == null || created >= cutoff
            }
            matchesQuery && matchesDate
        }
    }

    val listPaging = rememberAdminListPaging(
        initialSortKey = "name",
        resetKey = listOf(query, dateFilter),
    )
    val pageItems = remember(filtered, listPaging.sort, listPaging.page) {
        filtered.adminSortedPaged(listPaging.sort, listPaging.page) { key ->
            when (key) {
                "category" -> { p -> p.categoryLabel.lowercase() }
                "price" -> { p -> p.priceCents }
                "stock" -> { p -> p.stockQty }
                "active" -> { p -> if (p.active) 1 else 0 }
                else -> { p -> p.name.lowercase() }
            }
        }
    }

    val total = filtered.size
    val low = filtered.count { it.stockLevel == AdminProductStockLevel.LOW }
    val out = filtered.count { it.stockLevel == AdminProductStockLevel.OUT }
    val active = filtered.count { it.active }

    BoxWithConstraints(
        Modifier
            .fillMaxSize()
            .background(AdminColors.Surface)
            .padding(GymlySpace.lg),
    ) {
        val compact = maxWidth < 700.dp
        Column(Modifier.fillMaxSize()) {
            ProductsHeader(
                dateFilter = dateFilter,
                showDateFilter = section == ProductsSection.Catalog,
                onFilter = { dateFilter = it },
            )
            Spacer(Modifier.height(12.dp))
            ProductsSectionTabs(section) { section = it }
            Spacer(Modifier.height(14.dp))
            if (section == ProductsSection.Categories) {
                AdminProductCategoriesPanel(onChanged = { reload() })
            } else {
            ProductsKpis(total, low, out, active, compact)
            Spacer(Modifier.height(14.dp))
            error?.let {
                Text(it, color = AdminColors.Error, fontSize = 13.sp, modifier = Modifier.padding(bottom = 8.dp))
            }
            ProductsDirectory(
                products = pageItems,
                totalCount = filtered.size,
                listPaging = listPaging,
                loading = loading,
                query = query,
                onQueryChange = { query = it },
                compact = compact,
                onCreate = { dialog = ProductsDialog.Create },
                onView = { dialog = ProductsDialog.Detail(it) },
                onEdit = { dialog = ProductsDialog.Edit(it) },
                onDelete = { dialog = ProductsDialog.Delete(it) },
            )
            }
        }
    }

    when (val d = dialog) {
        is ProductsDialog.Detail -> ProductDetailDialog(d.item) { dialog = null }
        is ProductsDialog.Create -> ProductFormDialog(
            title = tr.addProduct,
            initial = null,
            companyId = companyId,
            categories = categories,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.createProduct(upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage(tr.productCreateFailed)
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ProductsDialog.Edit -> ProductFormDialog(
            title = tr.editProduct,
            initial = d.item,
            companyId = companyId,
            categories = categories,
            saving = saving,
            onDismiss = { if (!saving) dialog = null },
            onSave = { upsert ->
                scope.launch {
                    saving = true
                    error = null
                    try {
                        AdminRepo.api.updateProduct(d.item.id, upsert)
                        dialog = null
                        reload()
                    } catch (e: Exception) {
                        if (pendingApprovals.offer(e)) {
                            dialog = null
                            error = null
                        } else {
                            error = e.userMessage(tr.productSaveFailed)
                        }
                    } finally {
                        saving = false
                    }
                }
            },
        )
        is ProductsDialog.Delete -> AlertDialog(
            onDismissRequest = { if (!saving) dialog = null },
            title = { Text(tr.deleteProduct) },
            text = {
                Text(tr.confirmDeleteProduct(d.item.name))
            },
            confirmButton = {
                Button(
                    onClick = {
                        scope.launch {
                            saving = true
                            error = null
                            try {
                                AdminRepo.api.deleteProduct(d.item.id)
                                dialog = null
                                reload()
                            } catch (e: Exception) {
                                if (pendingApprovals.offer(e)) {
                                dialog = null
                                error = null
                            } else {
                                error = e.userMessage(tr.productDeleteFailed)
                            }
                            } finally {
                                saving = false
                            }
                        }
                    },
                    enabled = !saving,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else t.common.delete)
                }
            },
            dismissButton = {
                TextButton(onClick = { dialog = null }, enabled = !saving) {
                    Text(t.common.cancel, color = AdminColors.TextMuted)
                }
            },
        )
        null -> Unit
    }
}

@Composable
private fun ProductsHeader(
    dateFilter: ProductsDateFilter,
    showDateFilter: Boolean,
    onFilter: (ProductsDateFilter) -> Unit,
) {
    val tr = storeReportsStrings()
    var menu by remember { mutableStateOf(false) }
    Row(
        Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top,
    ) {
        Column(Modifier.weight(1f)) {
            Text(tr.productsTitle, fontSize = 24.sp, fontWeight = FontWeight.Bold, color = AdminColors.Text)
            Text(
                tr.productsSubtitle,
                color = AdminColors.TextMuted,
                fontSize = 13.sp,
                modifier = Modifier.padding(top = 4.dp),
            )
        }
        if (showDateFilter) Box {
            OutlinedButton(onClick = { menu = true }, shape = RoundedCornerShape(10.dp)) {
                Text(tr.dateRangeLabel(dateFilter.days), color = AdminColors.Text)
            }
            DropdownMenu(expanded = menu, onDismissRequest = { menu = false }) {
                ProductsDateFilter.entries.forEach { opt ->
                    DropdownMenuItem(
                        text = { Text(tr.dateRangeLabel(opt.days)) },
                        onClick = {
                            onFilter(opt)
                            menu = false
                        },
                    )
                }
            }
        }
    }
}

@Composable
private fun ProductsSectionTabs(
    current: ProductsSection,
    onSelect: (ProductsSection) -> Unit,
) {
    Row(horizontalArrangement = Arrangement.spacedBy(8.dp)) {
        ProductsSection.entries.forEach { section ->
            val selected = section == current
            Text(
                section.label,
                color = if (selected) AdminColors.OnBrand else AdminColors.Text,
                fontWeight = FontWeight.SemiBold,
                fontSize = 14.sp,
                modifier = Modifier
                    .clip(RoundedCornerShape(10.dp))
                    .background(if (selected) AdminColors.Primary else AdminColors.SurfaceContainer)
                    .clickable { onSelect(section) }
                    .padding(horizontal = 14.dp, vertical = 8.dp),
            )
        }
    }
}

@Composable
private fun ProductsKpis(total: Int, low: Int, out: Int, active: Int, compact: Boolean) {
    val tr = storeReportsStrings()
    val cards = listOf(
        Triple(tr.kpiProducts, total.toString(), AdminColors.Primary),
        Triple(tr.kpiLowStock, low.toString(), Color(0xFFEF6C00)),
        Triple(tr.kpiOutOfStock, out.toString(), AdminColors.Error),
        Triple(tr.kpiActiveProducts, active.toString(), Color(0xFF2E7D32)),
    )
    if (compact) {
        // Celular: una franja de indicadores de una línea, para dejar el espacio a la lista.
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            cards.forEach { (label, value, accent) ->
                AdminCompactKpiChip(label, value, accent, Modifier.weight(1f))
            }
        }
    } else {
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(12.dp)) {
            cards.forEach { (label, value, accent) ->
                ProductKpiCard(label, value, accent, Modifier.weight(1f))
            }
        }
    }
}

@Composable
private fun ProductKpiCard(label: String, value: String, accent: Color, modifier: Modifier = Modifier) {
    Column(
        modifier
            .clip(RoundedCornerShape(14.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Spacer(Modifier.height(6.dp))
        Text(value, fontWeight = FontWeight.Bold, fontSize = 22.sp, color = accent)
    }
}

@Composable
private fun ColumnScope.ProductsDirectory(
    products: List<AdminProduct>,
    totalCount: Int,
    listPaging: AdminListPaging,
    loading: Boolean,
    query: String,
    onQueryChange: (String) -> Unit,
    compact: Boolean,
    onCreate: () -> Unit,
    onView: (AdminProduct) -> Unit,
    onEdit: (AdminProduct) -> Unit,
    onDelete: (AdminProduct) -> Unit,
) {
    val tr = storeReportsStrings()
    Column(
        Modifier
            .weight(1f)
            .fillMaxWidth()
            .clip(RoundedCornerShape(16.dp))
            .background(AdminColors.SurfaceContainer)
            .padding(16.dp),
    ) {
        if (compact) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                Text(tr.productCatalog, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                ProductSearchField(query, onQueryChange)
                if (canWriteModule(AdminModuleAccess.PRODUCTS)) Button(
                    onClick = onCreate,
                    modifier = Modifier.fillMaxWidth().height(44.dp),
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(tr.addProductButton, fontWeight = FontWeight.SemiBold)
                }
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Text(tr.productCatalog, fontWeight = FontWeight.Bold, fontSize = 16.sp)
                Row(horizontalArrangement = Arrangement.spacedBy(10.dp), verticalAlignment = Alignment.CenterVertically) {
                    Box(Modifier.widthIn(max = 220.dp)) { ProductSearchField(query, onQueryChange) }
                    if (canWriteModule(AdminModuleAccess.PRODUCTS)) Button(
                        onClick = onCreate,
                        shape = RoundedCornerShape(10.dp),
                        colors = ButtonDefaults.buttonColors(
                            containerColor = AdminColors.Primary,
                            contentColor = AdminColors.OnBrand,
                        ),
                    ) {
                        Text("+ Agregar producto", fontWeight = FontWeight.SemiBold)
                    }
                }
            }
        }
        Spacer(Modifier.height(14.dp))
        when {
            loading -> Box(Modifier.weight(1f).fillMaxWidth().padding(32.dp), contentAlignment = Alignment.Center) {
                CircularProgressIndicator(color = AdminColors.Primary)
            }
            totalCount == 0 -> Box(Modifier.weight(1f).fillMaxWidth()) {
                Text("No hay productos todavía.", color = AdminColors.TextMuted)
            }
            compact -> Column(Modifier.weight(1f).fillMaxWidth()) {
                VerticalScrollColumn(
                    Modifier.weight(1f).fillMaxWidth(),
                    verticalArrangement = Arrangement.spacedBy(10.dp),
                ) {
                    products.forEach { p -> ProductMobileCard(p, onView, onEdit, onDelete) }
                }
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
            else -> Column(Modifier.weight(1f).fillMaxWidth()) {
                ProductsTable(
                    products,
                    listPaging = listPaging,
                    onView,
                    onEdit,
                    onDelete,
                    modifier = Modifier.weight(1f).fillMaxWidth(),
                )
                AdminListPaginationBar(
                    totalItems = totalCount,
                    page = listPaging.page,
                    onPageChange = { listPaging.page = it },
                )
            }
        }
    }
}

@Composable
private fun ProductSearchField(query: String, onQueryChange: (String) -> Unit) {
    OutlinedTextField(
        value = query,
        onValueChange = onQueryChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        placeholder = { Text("Buscar...", color = AdminColors.TextMuted) },
        leadingIcon = { Icon(Icons.Outlined.Search, null, tint = AdminColors.TextMuted) },
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}

@Composable
private fun ProductsTable(
    products: List<AdminProduct>,
    listPaging: AdminListPaging,
    onView: (AdminProduct) -> Unit,
    onEdit: (AdminProduct) -> Unit,
    onDelete: (AdminProduct) -> Unit,
    modifier: Modifier = Modifier,
) {
    Column(modifier.fillMaxWidth()) {
        Row(
            Modifier.fillMaxWidth().padding(vertical = 8.dp, horizontal = 4.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(4.dp),
        ) {
            AdminSortableHeader("Producto", "name", listPaging.sort, listPaging::onSort, Modifier.weight(1.5f))
            AdminSortableHeader("Categoría", "category", listPaging.sort, listPaging::onSort, Modifier.weight(1.1f))
            AdminSortableHeader("Precio", "price", listPaging.sort, listPaging::onSort, Modifier.weight(1f))
            AdminSortableHeader("Stock", "stock", listPaging.sort, listPaging::onSort, Modifier.weight(0.8f))
            AdminSortableHeader("Activo", "active", listPaging.sort, listPaging::onSort, Modifier.weight(0.7f))
            AdminListHeaderLabel("Acciones", Modifier.width(140.dp))
        }
        HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.5f))
        VerticalScrollColumn(Modifier.weight(1f).fillMaxWidth(),
        ) {
            products.forEach { p ->
                Row(
                    Modifier.fillMaxWidth().padding(vertical = 10.dp, horizontal = 4.dp),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(4.dp),
                ) {
                    Row(Modifier.weight(1.5f), verticalAlignment = Alignment.CenterVertically) {
                        ProductDot(p)
                        Spacer(Modifier.width(10.dp))
                        Column {
                            Text(p.name, fontWeight = FontWeight.SemiBold, color = AdminColors.Text)
                        }
                    }
                    Box(Modifier.weight(1.1f)) { CategoryBadge(p.categoryLabel) }
                    Text(productPriceLabel(p), Modifier.weight(1f), color = AdminColors.Text, fontSize = 13.sp, maxLines = 1)
                    Box(Modifier.weight(0.8f)) { StockCell(p) }
                    Box(Modifier.weight(0.7f)) { ActiveBadge(p.active) }
                    Row(Modifier.width(140.dp), horizontalArrangement = Arrangement.spacedBy(2.dp)) {
                        IconButton(onClick = { onView(p) }) {
                            Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
                        }
                        if (canWriteModule(AdminModuleAccess.PRODUCTS)) IconButton(onClick = { onEdit(p) }) {
                            Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
                        }
                        if (canDeleteModule(AdminModuleAccess.PRODUCTS)) IconButton(onClick = { onDelete(p) }) {
                            Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
                        }
                    }
                }
                HorizontalDivider(color = AdminColors.Outline.copy(alpha = 0.35f))
            }
        }
    }
}

@Composable
private fun ProductMobileCard(
    item: AdminProduct,
    onView: (AdminProduct) -> Unit,
    onEdit: (AdminProduct) -> Unit,
    onDelete: (AdminProduct) -> Unit,
) {
    Column(
        Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(12.dp))
            .border(1.dp, AdminColors.Outline.copy(alpha = 0.6f), RoundedCornerShape(12.dp))
            .padding(12.dp),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            ProductDot(item)
            Spacer(Modifier.width(10.dp))
            Column(Modifier.weight(1f)) {
                Text(item.name, fontWeight = FontWeight.SemiBold)
            }
            ActiveBadge(item.active)
        }
        Spacer(Modifier.height(8.dp))
        CategoryBadge(item.categoryLabel)
        Spacer(Modifier.height(6.dp))
        Row(horizontalArrangement = Arrangement.spacedBy(12.dp), verticalAlignment = Alignment.CenterVertically) {
            Text(productPriceLabel(item), fontSize = 13.sp)
            StockCell(item)
        }
        Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
            IconButton(onClick = { onView(item) }) {
                Icon(Icons.Outlined.Search, "Ver", tint = AdminColors.TextMuted)
            }
            if (canWriteModule(AdminModuleAccess.PRODUCTS)) IconButton(onClick = { onEdit(item) }) {
                Icon(Icons.Outlined.Edit, "Editar", tint = AdminColors.TextMuted)
            }
            if (canDeleteModule(AdminModuleAccess.PRODUCTS)) IconButton(onClick = { onDelete(item) }) {
                Icon(Icons.Outlined.Delete, "Eliminar", tint = AdminColors.Error)
            }
        }
    }
}

@Composable
private fun ProductDot(item: AdminProduct) {
    val color = when {
        !item.active -> AdminColors.TextMuted
        item.stockLevel == AdminProductStockLevel.OUT -> AdminColors.Error
        item.stockLevel == AdminProductStockLevel.LOW -> Color(0xFFEF6C00)
        else -> Color(0xFF2E7D32)
    }
    Box(Modifier.size(10.dp).clip(CircleShape).background(color))
}

@Composable
private fun CategoryBadge(label: String) {
    val (fg, bg) = when (label) {
        "POP / mercancía" -> Color(0xFF5E35B1) to Color(0xFFEDE7F6)
        "Equipamiento del gimnasio" -> Color(0xFF1565C0) to Color(0xFFE3F2FD)
        "Alimentos y suplementos" -> Color(0xFF2E7D32) to Color(0xFFE8F5E9)
        else -> {
            val palette = listOf(
                Color(0xFF6A1B9A) to Color(0xFFF3E5F5),
                Color(0xFF00695C) to Color(0xFFE0F2F1),
                Color(0xFFEF6C00) to Color(0xFFFFF3E0),
                Color(0xFF455A64) to Color(0xFFECEFF1),
            )
            palette[(label.hashCode() and Int.MAX_VALUE) % palette.size]
        }
    }
    Text(
        label,
        color = fg,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(bg)
            .padding(horizontal = 8.dp, vertical = 4.dp),
    )
}

@Composable
private fun StockCell(item: AdminProduct) {
    Column {
        StockBadge(item)
        Text("mín. ${item.minStockQty}", color = AdminColors.TextMuted, fontSize = 12.sp)
    }
}

private fun productPriceLabel(item: AdminProduct): String {
    val bs = formatMoney(item.priceCents.toInt(), item.currency)
    val usd = formatMoney(item.priceUsdCents.toInt(), "USD")
    return "$bs · $usd"
}

@Composable
private fun StockBadge(item: AdminProduct) {
    when (item.stockLevel) {
        AdminProductStockLevel.OUT -> Text(
            "Agotado",
            color = Color(0xFFB71C1C),
            fontSize = 12.sp,
            fontWeight = FontWeight.SemiBold,
            modifier = Modifier
                .clip(RoundedCornerShape(8.dp))
                .background(Color(0xFFFFEBEE))
                .padding(horizontal = 8.dp, vertical = 4.dp),
        )
        AdminProductStockLevel.LOW -> Text(
            item.stockQty.toString(),
            color = Color(0xFFE65100),
            fontSize = 12.sp,
            fontWeight = FontWeight.Bold,
            modifier = Modifier
                .clip(CircleShape)
                .background(Color(0xFFFFF3E0))
                .padding(horizontal = 10.dp, vertical = 6.dp),
        )
        AdminProductStockLevel.OK -> Text(item.stockQty.toString(), color = AdminColors.Text, fontSize = 13.sp)
    }
}

@Composable
private fun ActiveBadge(active: Boolean) {
    Text(
        if (active) "Activo" else "Inactivo",
        color = if (active) Color(0xFF1B5E20) else AdminColors.TextMuted,
        fontSize = 12.sp,
        fontWeight = FontWeight.SemiBold,
        modifier = Modifier
            .clip(RoundedCornerShape(8.dp))
            .background(if (active) Color(0xFFE8F5E9) else AdminColors.Outline.copy(alpha = 0.25f))
            .padding(horizontal = 10.dp, vertical = 4.dp),
    )
}

@Composable
private fun ProductDetailDialog(item: AdminProduct, onDismiss: () -> Unit) {
    Dialog(onDismissRequest = onDismiss) {
        Column(
            Modifier
                .widthIn(max = 440.dp)
                .fillMaxWidth()
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text("Detalle del producto", fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            DetailLine("Nombre del producto", item.name)
            DetailLine("Categoría", item.categoryLabel)
            DetailLine("Precio Bs", formatMoney(item.priceCents.toInt(), item.currency))
            DetailLine("Precio $", formatMoney(item.priceUsdCents.toInt(), "USD"))
            DetailLine("Cantidad", if (item.stockLevel == AdminProductStockLevel.OUT) "Agotado" else item.stockQty.toString())
            DetailLine("Cantidad mínima", item.minStockQty.toString())
            DetailLine("Activo", if (item.active) "Activo" else "Inactivo")
            item.description?.takeIf { it.isNotBlank() }?.let { DetailLine("Descripción", it) }
            Spacer(Modifier.height(12.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.End) {
                Button(
                    onClick = onDismiss,
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) { Text("Cerrar") }
            }
        }
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    Column(Modifier.padding(bottom = 10.dp)) {
        Text(label, color = AdminColors.TextMuted, fontSize = 12.sp)
        Text(value, color = AdminColors.Text, fontSize = 14.sp, fontWeight = FontWeight.Medium)
    }
}

@Composable
private fun ProductFormDialog(
    title: String,
    initial: AdminProduct?,
    companyId: String?,
    categories: List<AdminProductCategoryItem>,
    saving: Boolean,
    onDismiss: () -> Unit,
    onSave: (AdminProductUpsert) -> Unit,
) {
    var name by remember(initial) { mutableStateOf(initial?.name.orEmpty()) }
    var categoryId by remember(initial, categories) {
        mutableStateOf(initial?.category ?: categories.firstOrNull()?.id.orEmpty())
    }
    var priceBs by remember(initial) { mutableStateOf(initial?.let { centsToEurosInput(it.priceCents) } ?: "0,00") }
    var priceUsd by remember(initial) { mutableStateOf(initial?.let { centsToEurosInput(it.priceUsdCents) } ?: "0,00") }
    var stock by remember(initial) { mutableStateOf(initial?.stockQty?.toString() ?: "0") }
    var minStock by remember(initial) { mutableStateOf(initial?.minStockQty?.toString() ?: "0") }
    val branchId = companyId
    var description by remember(initial) { mutableStateOf(initial?.description.orEmpty()) }
    var active by remember(initial) { mutableStateOf(initial?.active ?: true) }
    var categoryMenu by remember { mutableStateOf(false) }
    var formError by remember { mutableStateOf<String?>(null) }

    // Ancho propio (no el de la plataforma) para que los campos queden en dos columnas.
    Dialog(
        onDismissRequest = { if (!saving) onDismiss() },
        properties = androidx.compose.ui.window.DialogProperties(usePlatformDefaultWidth = false),
    ) {
        Column(
            Modifier
                // widthIn va antes de fillMaxWidth: así el tope sí se aplica en escritorio.
                .widthIn(max = 620.dp)
                .fillMaxWidth(0.92f)
                .clip(RoundedCornerShape(16.dp))
                .background(AdminColors.SurfaceContainer)
                .padding(20.dp)
                .verticalScroll(rememberScrollState()),
        ) {
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.SpaceBetween, verticalAlignment = Alignment.CenterVertically) {
                Text(title, fontWeight = FontWeight.Bold, fontSize = 18.sp)
                IconButton(onClick = onDismiss, enabled = !saving) {
                    Icon(Icons.Outlined.Close, "Cerrar", tint = AdminColors.TextMuted)
                }
            }
            Spacer(Modifier.height(12.dp))
            // Dos columnas (escritorio): izquierda = identificación y precios; derecha = stock, descripción y estado.
            // En celular (ancho < 600 dp) se apilan en una sola columna.
            BoxWithConstraints(Modifier.fillMaxWidth()) {
                val stacked = maxWidth < 500.dp
                val leftFields: @Composable () -> Unit = {
                    FieldLabel("Nombre del producto *")
                    FormField(name, { name = it })
                    Spacer(Modifier.height(10.dp))
                    FieldLabel("Categoría")
                    Box {
                        val selectedName = categories.firstOrNull { it.id == categoryId }?.name ?: "Sin categoría"
                        OutlinedButton(onClick = { categoryMenu = true }, modifier = Modifier.fillMaxWidth(), enabled = !saving && categories.isNotEmpty(), shape = RoundedCornerShape(10.dp)) {
                            Text(selectedName, color = AdminColors.Text, modifier = Modifier.fillMaxWidth())
                        }
                        DropdownMenu(expanded = categoryMenu, onDismissRequest = { categoryMenu = false }) {
                            categories.forEach { c ->
                                DropdownMenuItem(text = { Text(c.name) }, onClick = { categoryId = c.id; categoryMenu = false })
                            }
                        }
                    }
                    Spacer(Modifier.height(10.dp))
                    FieldLabel("Precio (Bs) *")
                    FormField(priceBs, { priceBs = it })
                    Spacer(Modifier.height(10.dp))
                    FieldLabel("Precio ($) *")
                    FormField(priceUsd, { priceUsd = it })
                }
                val rightFields: @Composable () -> Unit = {
                    FieldLabel("Cantidad *")
                    FormField(stock, { stock = it.filter { ch -> ch.isDigit() }.take(6) })
                    Spacer(Modifier.height(10.dp))
                    FieldLabel("Cantidad mínima *")
                    FormField(minStock, { minStock = it.filter { ch -> ch.isDigit() }.take(6) })
                    Spacer(Modifier.height(10.dp))
                    FieldLabel("Descripción")
                    OutlinedTextField(
                        value = description,
                        onValueChange = { description = it },
                        modifier = Modifier.fillMaxWidth().heightIn(min = 88.dp),
                        placeholder = { Text("Uso, presentación u otras notas", color = AdminColors.TextMuted) },
                        shape = RoundedCornerShape(10.dp),
                        colors = OutlinedTextFieldDefaults.colors(
                            focusedBorderColor = AdminColors.Primary,
                            unfocusedBorderColor = AdminColors.Outline,
                        ),
                    )
                    Spacer(Modifier.height(10.dp))
                    Row(verticalAlignment = Alignment.CenterVertically) {
                        Checkbox(
                            checked = active,
                            onCheckedChange = { active = it },
                            enabled = !saving,
                            colors = CheckboxDefaults.colors(checkedColor = AdminColors.Primary),
                        )
                        Text("Activo (disponible para la venta)", color = AdminColors.Text, fontSize = 13.sp)
                    }
                }
                if (stacked) {
                    Column(Modifier.fillMaxWidth()) {
                        leftFields()
                        Spacer(Modifier.height(10.dp))
                        rightFields()
                    }
                } else {
                    Row(
                        Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(20.dp),
                        verticalAlignment = Alignment.Top,
                    ) {
                        Column(Modifier.weight(1f)) { leftFields() }
                        Column(Modifier.weight(1f)) { rightFields() }
                    }
                }
            }
            formError?.let {
                Spacer(Modifier.height(8.dp))
                Text(it, color = AdminColors.Error, fontSize = 13.sp)
            }
            Spacer(Modifier.height(16.dp))
            Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(10.dp, Alignment.End)) {
                OutlinedButton(onClick = onDismiss, enabled = !saving, shape = RoundedCornerShape(10.dp)) {
                    Text("Cancelar", color = AdminColors.TextMuted)
                }
                Button(
                    onClick = {
                        formError = null
                        if (name.trim().isEmpty()) {
                            formError = "El nombre es obligatorio."
                            return@Button
                        }
                        if (categoryId.isBlank()) {
                            formError = "Elige una categoría. Puedes crearlas en la pestaña Categorías."
                            return@Button
                        }
                        val cents = eurosToCents(priceBs)
                        if (cents == null) {
                            formError = "Precio en Bs inválido."
                            return@Button
                        }
                        val usdCents = eurosToCents(priceUsd)
                        if (usdCents == null) {
                            formError = "Precio en $ inválido."
                            return@Button
                        }
                        if (usdCents <= 0L) {
                            formError = "El precio en $ es obligatorio: las ventas de tienda cobran en Bs y en USD."
                            return@Button
                        }
                        val qty = stock.toIntOrNull()
                        if (qty == null || qty < 0) {
                            formError = "Cantidad inválida."
                            return@Button
                        }
                        val minimum = minStock.toIntOrNull()
                        if (minimum == null || minimum < 0) {
                            formError = "Cantidad mínima inválida."
                            return@Button
                        }
                        onSave(
                            AdminProductUpsert(
                                name = name.trim(),
                                category = categoryId,
                                priceCents = cents,
                                priceUsdCents = usdCents,
                                stockQty = qty,
                                minStockQty = minimum,
                                description = description.trim().ifBlank { null },
                                active = active,
                                branchId = branchId,
                            ),
                        )
                    },
                    enabled = !saving,
                    shape = RoundedCornerShape(10.dp),
                    colors = ButtonDefaults.buttonColors(
                        containerColor = AdminColors.Primary,
                        contentColor = AdminColors.OnBrand,
                    ),
                ) {
                    Text(if (saving) "…" else "Guardar", fontWeight = FontWeight.SemiBold)
                }
            }
        }
    }
}

@Composable
private fun FormPair(
    left: @Composable () -> Unit,
    right: @Composable () -> Unit,
) {
    BoxWithConstraints(Modifier.fillMaxWidth()) {
        if (maxWidth < 560.dp) {
            Column(Modifier.fillMaxWidth()) {
                left()
                Spacer(Modifier.height(10.dp))
                right()
            }
        } else {
            Row(
                Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(16.dp),
                verticalAlignment = Alignment.Top,
            ) {
                Column(Modifier.weight(1f)) { left() }
                Column(Modifier.weight(1f)) { right() }
            }
        }
    }
    Spacer(Modifier.height(10.dp))
}

@Composable
private fun FieldLabel(text: String) {
    Text(text, color = AdminColors.TextMuted, fontSize = 12.sp)
    Spacer(Modifier.height(4.dp))
}

@Composable
private fun FormField(value: String, onChange: (String) -> Unit) {
    OutlinedTextField(
        value = value,
        onValueChange = onChange,
        modifier = Modifier.fillMaxWidth(),
        singleLine = true,
        shape = RoundedCornerShape(10.dp),
        colors = OutlinedTextFieldDefaults.colors(
            focusedBorderColor = AdminColors.Primary,
            unfocusedBorderColor = AdminColors.Outline,
        ),
    )
}
